import { useEffect, useState } from "react";
import type { Deal, RehabItem, ComplianceItem, ComplianceStatus, EstimatorRole } from "../types";
import {
  REHAB_CATEGORIES,
  ESTIMATOR_ROLES,
  DEFAULT_CONTINGENCY,
  rehabSubtotal,
  contingencyAmount,
  rehabTotal,
  withCompliance,
  blankItem,
  complianceProgress,
} from "../lib/rehab";
import { listMembers, isAssigned, toggleAssignee, type TeamMember } from "../lib/team";
import { money0 } from "../lib/format";

const STATUS_META: Record<ComplianceStatus, { cls: string; label: string }> = {
  todo: { cls: "t-watch", label: "Open" },
  clear: { cls: "t-pass", label: "Clear" },
  issue: { cls: "t-fail", label: "Issue" },
};

/**
 * The operational layer for one deal. Your team submits costs as structured
 * line items in YOUR format; a contingency buffer is auto-added and the buffered
 * total flows into Deal.rehab. A compliance checklist tracks court/permits/
 * regulatory gates so nothing closes with an open liability.
 */
export function RehabPlan({ deal, onChange }: { deal: Deal; onChange: (patch: Partial<Deal>) => void }) {
  const items = deal.rehabItems ?? [];
  const pct = deal.contingencyPct ?? DEFAULT_CONTINGENCY;
  const compliance = withCompliance(deal);

  const [members, setMembers] = useState<TeamMember[]>([]);
  useEffect(() => { listMembers().then(setMembers).catch(() => {}); }, []);
  const assignedCount = (deal.assignees ?? []).length;

  // Recompute the buffered rehab whenever the team's numbers change, so the
  // estimates feed the underwriting automatically.
  const commit = (nextItems: RehabItem[], nextPct = pct) => {
    const patch: Partial<Deal> = { rehabItems: nextItems, contingencyPct: nextPct };
    if (nextItems.length) patch.rehab = rehabTotal(nextItems, nextPct);
    onChange(patch);
  };

  const setItem = (id: string, field: keyof RehabItem, value: string | number) =>
    commit(items.map((it) => (it.id === id ? { ...it, [field]: value } : it)));
  const addItem = () => commit([...items, blankItem()]);
  const removeItem = (id: string) => commit(items.filter((it) => it.id !== id));
  const setPct = (p: number) => commit(items, p);

  const setCompliance = (id: string, field: keyof ComplianceItem, value: string) =>
    onChange({ compliance: compliance.map((c) => (c.id === id ? { ...c, [field]: value } : c)) });

  const subtotal = rehabSubtotal(items);
  const buffer = contingencyAmount(items, pct);
  const total = rehabTotal(items, pct);
  const prog = complianceProgress(compliance);

  return (
    <>
      <div className="pe">
        <div className="l">◆ In plain English</div>
        <p>
          This is where your <b>team feeds the deal — in your format</b>. Your general contractor, inspector, or estimator
          enters each cost as a line item; Beacon adds an automatic <b>{Math.round(pct * 100)}% buffer</b> on top of every
          estimate to protect you from under-quotes, and the buffered total becomes the deal's rehab number everywhere else.
          The compliance checklist keeps court, permit, and regulatory liabilities from slipping through to closing.
        </p>
      </div>

      {/* ASSIGN TEAM */}
      <div className="card pad" style={{ marginBottom: 12, borderLeft: "4px solid var(--brass)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div className="eyebrow">Assign this deal to your team</div>
          <span className={"tag " + (assignedCount ? "t-pass" : "t-watch")}>{assignedCount ? `${assignedCount} assigned` : "None assigned"}</span>
        </div>
        {members.length === 0 ? (
          <div className="note" style={{ marginTop: 10 }}>
            No teammates yet. Invite your contractor or inspector from the <b>👥 Team</b> tab, then assign them here to have
            them submit their scope directly.
          </div>
        ) : (
          <>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
              {members.map((mem) => {
                const on = isAssigned(deal, mem.id);
                return (
                  <button key={mem.id} className={"chip-toggle" + (on ? " on" : "")} onClick={() => onChange({ assignees: toggleAssignee(deal, mem.id) })}>
                    {on ? "✓ " : "＋ "}{mem.name || mem.email} <span style={{ opacity: 0.7 }}>· {mem.role}</span>
                  </button>
                );
              })}
            </div>
            <div className="note" style={{ marginTop: 8 }}>Assigned teammates see this property in their own seat and can submit their numbers — which land in the cost table below.</div>
          </>
        )}
      </div>

      {/* COST INTAKE */}
      <div className="card pad" style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div className="eyebrow">Team cost estimates — your scope, your format</div>
          <button className="btn btn-a btn-sm noprint" onClick={addItem}>+ Add line item</button>
        </div>

        {items.length === 0 ? (
          <div className="note" style={{ marginTop: 10 }}>
            No estimates yet. Add a line item, or share this deal with your contractor/inspector and have them fill their
            scope — every number they put in is buffered {Math.round(pct * 100)}% and flows straight into the underwriting.
          </div>
        ) : (
          <div className="tablewrap" style={{ marginTop: 10 }}>
            <table className="t">
              <thead>
                <tr>
                  <th>Scope</th><th>Description</th><th>Who</th><th>Role</th>
                  <th className="n">Estimate</th><th className="n">+{Math.round(pct * 100)}%</th><th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id}>
                    <td>
                      <select className="rp-in" value={it.category} onChange={(e) => setItem(it.id, "category", e.target.value)}>
                        {REHAB_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </td>
                    <td>
                      <input className="rp-in" style={{ minWidth: 150 }} value={it.label} placeholder="e.g. full tear-off, 25 sq"
                        onChange={(e) => setItem(it.id, "label", e.target.value)} />
                    </td>
                    <td>
                      <input className="rp-in" style={{ minWidth: 110 }} value={it.source} placeholder="company / person"
                        onChange={(e) => setItem(it.id, "source", e.target.value)} />
                    </td>
                    <td>
                      <select className="rp-in" value={it.role} onChange={(e) => setItem(it.id, "role", e.target.value as EstimatorRole)}>
                        {ESTIMATOR_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </td>
                    <td className="n">
                      <input className="rp-in n" style={{ width: 92, textAlign: "right" }} type="number" min={0} value={it.cost || ""}
                        placeholder="0" onChange={(e) => setItem(it.id, "cost", Number(e.target.value) || 0)} />
                    </td>
                    <td className="n" style={{ color: "var(--brass-deep)", fontWeight: 600 }}>{money0(Math.round((it.cost || 0) * (1 + pct)))}</td>
                    <td><button className="rp-x noprint" title="Remove" onClick={() => removeItem(it.id)}>✕</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* TOTALS + CONTINGENCY */}
        <div className="grid g2" style={{ marginTop: 14 }}>
          <div>
            <div className="eyebrow" style={{ marginBottom: 6 }}>Contingency buffer — your protection</div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <input type="range" min={0} max={30} step={1} value={Math.round(pct * 100)}
                onChange={(e) => setPct(Number(e.target.value) / 100)} style={{ flex: 1 }} className="noprint" />
              <span className="mono" style={{ fontSize: 15, fontWeight: 700, color: "var(--forest)", minWidth: 44, textAlign: "right" }}>{Math.round(pct * 100)}%</span>
            </div>
            <div className="note">Auto-added to every estimate. Default 10% — raise it for older buildings or unknown scope.</div>
          </div>
          <div className="card pad" style={{ background: "var(--cream)", margin: 0 }}>
            <table className="t">
              <tbody>
                <tr><td>Team estimates (subtotal)</td><td className="n">{money0(subtotal)}</td></tr>
                <tr><td>+ {Math.round(pct * 100)}% contingency buffer</td><td className="n" style={{ color: "var(--brass-deep)" }}>+{money0(buffer)}</td></tr>
                <tr className="tot"><td>Protected rehab → feeds the deal</td><td className="n">{money0(total)}</td></tr>
              </tbody>
            </table>
          </div>
        </div>
        {items.length > 0 && (
          <div className="flag ok" style={{ marginTop: 10 }}>
            <span>✓</span><div>This <b>{money0(total)}</b> is now the deal's rehab cost — all-in cost, returns, DSCR, and the score already reflect it.</div>
          </div>
        )}
      </div>

      {/* COMPLIANCE */}
      <div className="card pad">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div className="eyebrow">Compliance — court · permits · regulatory</div>
          <span className={"tag " + (prog.issues ? "t-fail" : prog.clear === prog.total ? "t-pass" : "t-watch")}>
            {prog.issues ? `${prog.issues} issue${prog.issues > 1 ? "s" : ""}` : `${prog.clear}/${prog.total} clear`}
          </span>
        </div>
        <div className="tablewrap" style={{ marginTop: 10 }}>
          <table className="t">
            <thead><tr><th>Gate</th><th>Status</th><th>Notes</th></tr></thead>
            <tbody>
              {compliance.map((c) => (
                <tr key={c.id}>
                  <td style={{ fontWeight: 600, color: "var(--forest)" }}>{c.label}</td>
                  <td>
                    <select className="rp-in" value={c.status} onChange={(e) => setCompliance(c.id, "status", e.target.value)}
                      style={{ borderColor: c.status === "issue" ? "var(--fail)" : c.status === "clear" ? "var(--pass)" : "var(--line)" }}>
                      <option value="todo">Open</option>
                      <option value="clear">Clear</option>
                      <option value="issue">Issue</option>
                    </select>
                    <span className={"tag " + STATUS_META[c.status].cls} style={{ marginLeft: 6 }}>{STATUS_META[c.status].label}</span>
                  </td>
                  <td><input className="rp-in" style={{ minWidth: 180 }} value={c.notes ?? ""} placeholder="ref #, who's clearing it, date"
                    onChange={(e) => setCompliance(c.id, "notes", e.target.value)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {prog.issues > 0 && (
          <div className="flag bad" style={{ marginTop: 10 }}>
            <span>!</span><div>{prog.issues} open compliance issue{prog.issues > 1 ? "s" : ""} — resolve before you close. These protect you from inheriting someone else's liability.</div>
          </div>
        )}
      </div>
    </>
  );
}
